// The plan draft editor's model: pure functions, no React, no network.
//
// The form holds every number as the text the person typed (rupees, percent, whole numbers); the
// content sent to the OMS holds minor units and basis points. The conversions split the string, so
// "1234.5" is 123450 and never parseFloat(x) * 100.
//
// Content paths follow the service (`benefits.2.label`, `pricingRule.addonRates.theft.capMinor`,
// `overrides.0.prices.monthly.12`) so a server issue lands under the field with the same path.
import CheckValid from "../../../Common/CheckValid";

export const BENEFIT_KINDS = [
  { value: "motor_extension", label: "Motor warranty extension" },
  { value: "battery_extension", label: "Battery warranty extension" },
  { value: "free_service", label: "Free service" },
  { value: "theft", label: "Theft cover" },
  { value: "roadside", label: "Roadside assistance" },
  { value: "coupon", label: "Coupon" },
];
export const CHARGE_MODELS = [
  { value: "included", label: "Included in the plan price" },
  { value: "monthly", label: "Paid monthly with the plan" },
  { value: "upfront", label: "Paid upfront as an add-on" },
];
export const ACTIVATIONS = [
  { value: "automatic", label: "Automatic" },
  { value: "manual_ops", label: "Manual, by operations" },
];
export const UNLOCK_TYPES = [
  { value: "purchase", label: "On purchase" },
  { value: "paid_months", label: "After paid months" },
  // 16 Oct 2026: the service's rule for free services (1, 3, 6 months after the plan starts).
  // Without it here, an edit loaded it as "On purchase" and saved it so.
  { value: "months_after_start", label: "Months after purchase" },
  { value: "date", label: "On a date" },
];
export const COUPON_MODES = [
  { value: "truncate_major", label: "Round down to whole rupees" },
  { value: "nearest_major", label: "Round to the nearest rupee" },
  { value: "up_to_ending", label: "Round up to an ending" },
  { value: "down_to_ending", label: "Round down to an ending" },
];
// What the coupon's share is taken of (14 Oct 2026). A rule saved before then has no basis and
// reads as the MRP.
export const COUPON_BASES = [
  { value: "cycle_mrp", label: "Cycle MRP" },
  { value: "plan_price", label: "Plan price (incl. GST)" },
];
// What a tier is priced from (14 Oct 2026). A rule saved before then has none and reads as the MRP.
// website_price (17 Oct 2026, Sachin): the price the website shows, for every cycle.
export const CYCLE_VALUE_BASES = [
  { value: "cycle_mrp", label: "Cycle MRP" },
  { value: "website_paid_price", label: "Website paid price (else MRP)" },
  { value: "website_price", label: "Website price (every cycle, else MRP)" },
];
export const cycleValueBasisLabel = (value) =>
  (CYCLE_VALUE_BASES.find((basis) => basis.value === value) || CYCLE_VALUE_BASES[0]).label;
export const EXTENSION_GRANTS = [
  { value: "paid_months_capped", label: "Grows with paid months" },
  { value: "full_on_completion", label: "Full once all months are paid" },
];

const TIER_KEY_LIKE = /^[a-z][a-z0-9_]{0,31}$/;
const TERMS_PATTERN = /^[A-Za-z0-9._-]{1,64}$/;
const ELIGIBILITY_DAYS_MAX = 90;
const EXTENSION_KINDS = ["motor_extension", "battery_extension"];

// App content (the Amigo Extend warranty screens). Limits follow the service.
export const DISPLAY_LIMITS = {
  tagline: 40,
  heroTitle: 80,
  heroSubtitle: 120,
  bannerTitle: 80,
  bannerSubtitle: 120,
  description: 4000,
  notCoveredItems: 20,
  notCoveredItem: 200,
  termsUrl: 500,
  benefitTitle: 80,
  benefitSubtitle: 60,
};
// em-platform refuses 60: the web backend caps a coupon's expiry at 5 years ahead.
export const VALIDITY_MONTHS_MAX = 59;

// What a draft from an older em-platform (no `display` at all) opens with.
export const DEFAULT_DISPLAY = Object.freeze({
  tagline: null,
  bestValue: false,
  hero: null,
  upgradeBanner: null,
  details: Object.freeze({ description: null, notCovered: Object.freeze([]) }),
  termsUrl: null,
});

// The service's own wording for some issue codes is meant for engineers; these read better here.
export const BEST_VALUE_TAKEN_MESSAGE = "Another tier is already marked Best value. Turn it off there first.";
const ISSUE_TEXT = { best_value_taken: BEST_VALUE_TAKEN_MESSAGE };

// The service's issues with the plain sentence for a code this screen knows.
export const plainIssues = (issues) =>
  (Array.isArray(issues) ? issues : []).map((issue) =>
    issue && ISSUE_TEXT[issue.code] !== undefined ? { ...issue, message: ISSUE_TEXT[issue.code] } : issue
  );

// ------------------------------------------------------------------ number conversions

// "1234.5" -> 123450, "7" -> 700, ".5" -> 50, "1." -> 100. Anything else (blank, a sign, three
// decimals, letters) -> null. Used for rupees (hundredths of a rupee) and percent (basis points).
export const decimalToHundredths = (text) => {
  if (typeof text !== "string") {
    return null;
  }
  const match = /^(\d*)(?:\.(\d{0,2}))?$/.exec(text.trim());
  if (match === null) {
    return null;
  }
  const whole = match[1];
  const fraction = match[2] === undefined ? "" : match[2];
  if (whole === "" && fraction === "") {
    return null;
  }
  if (whole.length > 12) {
    return null;
  }
  return Number(whole === "" ? "0" : whole) * 100 + Number(fraction.padEnd(2, "0"));
};

// 123450 -> "1234.5", 123400 -> "1234", 5 -> "0.05".
export const hundredthsToDecimal = (value) => {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return "";
  }
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(Math.trunc(value));
  const whole = Math.trunc(abs / 100);
  const fraction = String(abs % 100)
    .padStart(2, "0")
    .replace(/0+$/, "");
  return `${sign}${whole}${fraction === "" ? "" : `.${fraction}`}`;
};

export const rupeesToMinor = decimalToHundredths;
export const minorToRupees = hundredthsToDecimal;
export const percentToBps = decimalToHundredths;
export const bpsToPercent = hundredthsToDecimal;

export const wholeToInt = (text) => {
  if (typeof text !== "string" || /^\d{1,9}$/.test(text.trim()) === false) {
    return null;
  }
  return Number(text.trim());
};

// The tenure months the form holds right now, as numbers. A factor or instalment kept under a key
// that is not one of them (a tenure that was cleared or renamed) is not part of the content: it
// would reach the service under a blank or unpadded key and the draft could not be published.
const heldTenures = (form) =>
  new Set(form.tenures.map((tenure) => wholeToInt(tenure)).filter((months) => months !== null));
// The content key ("12") for a form key ("012"), or null when no tenure holds it.
const contentTenureKey = (key, held) => {
  const months = wholeToInt(key);
  return months !== null && held.has(months) ? String(months) : null;
};

// ------------------------------------------------------------------ content -> form

const str = (value) => (typeof value === "string" ? value : "");
const whole = (value) => (Number.isInteger(value) ? String(value) : "");
const rupees = (value) => (Number.isInteger(value) ? minorToRupees(value) : "");
const percent = (value) => (Number.isInteger(value) ? bpsToPercent(value) : "");

export const emptyBenefit = () => ({
  key: "",
  kind: "free_service",
  label: "",
  title: "",
  subtitle: "",
  worth: "",
  chargeModel: "included",
  termMonths: "",
  followsPlanState: false,
  activation: "automatic",
  unlockType: "purchase",
  unlockMonths: "",
  unlockImmediate: false,
  unlockDate: "",
  bundledWith: "",
});

export const emptyOverride = () => ({
  productGroup: "",
  oneTime: "",
  couponValue: "",
  monthly: {},
  addons: {},
});

// ------------------------------------------------------------------ bike segments (14 Oct 2026)
//
// A plan can price by bike segment (X Factor, Desire): each segment lists website parent models
// and has its own all-in one-time price and dealer margin. A bike whose model no segment lists
// falls into the default segment. Product-group overrides still win over segments.

// The website models the "Start with X Factor and Desire" button puts in Desire, when listed.
export const STARTER_DESIRE_MODELS = ["doodle-pro", "doodle", "viper", "ranger"];

// "X Factor" -> "x_factor". Only a new segment's key follows its name: a stored key never changes,
// because the service and the published versions know the segment by it.
export const segmentKeyFromName = (name) => {
  let key = (typeof name === "string" ? name : "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  if (key !== "" && /^[a-z]/.test(key) === false) {
    key = `s_${key}`;
  }
  return key.slice(0, 32).replace(/_+$/, "");
};

// `isNew` marks a segment added in this editor (its key can still change); `keyEdited` is set once
// the person types the key, so the name stops rewriting it.
export const emptySegment = (isDefault = false) => ({
  key: "",
  name: "",
  isNew: true,
  keyEdited: false,
  isDefault,
  models: [],
  oneTime: "",
  dealerMargin: "",
  seriesRank: "",
  monthly: {},
  addons: {},
});

// The two segments Sachin asked for, prices left blank for staff to fill. `models` is the website
// model list ({ slug, name }); Desire takes whichever of its usual models are in it.
export const starterSegments = (models) => {
  const listed = new Set((Array.isArray(models) ? models : []).map((model) => model.slug));
  return [
    { ...emptySegment(true), key: "x_factor", name: "X Factor", seriesRank: "1" },
    {
      ...emptySegment(false),
      key: "desire",
      name: "Desire",
      seriesRank: "2",
      models: STARTER_DESIRE_MODELS.filter((slug) => listed.has(slug)),
    },
  ];
};

// The website parent models from the product groups answer: [{ slug, name }], empty when the
// service sends none (an em-platform from before bike segments).
export const websiteModelsFrom = (data) =>
  (Array.isArray(data?.models) ? data.models : [])
    .filter((model) => model && typeof model.slug === "string" && model.slug !== "")
    .map((model) => ({
      slug: model.slug,
      name: typeof model.name === "string" && model.name !== "" ? model.name : model.slug,
    }));

// A model's name from the website model list, or its slug when the list does not have it.
export const modelName = (models, slug) => {
  const found = (Array.isArray(models) ? models : []).find((model) => model.slug === slug);
  return found ? found.name : slug;
};

const benefitToForm = (benefit) => {
  const unlock = benefit?.unlock || { type: "purchase" };
  return {
    key: str(benefit?.key),
    kind: str(benefit?.kind),
    label: str(benefit?.label),
    title: str(benefit?.title),
    subtitle: str(benefit?.subtitle),
    worth: rupees(benefit?.worthMinor),
    chargeModel: str(benefit?.chargeModel),
    termMonths: whole(benefit?.termMonths),
    followsPlanState: benefit?.followsPlanState === true,
    activation: str(benefit?.activation),
    unlockType: str(unlock.type) || "purchase",
    unlockMonths: whole(unlock.months),
    unlockImmediate: unlock.immediateOnOneTime === true,
    unlockDate: str(unlock.on),
    // 14 Oct 2026: a roadside riding on the theft line ("Theft + RSA", one component).
    bundledWith: str(benefit?.bundledWith),
  };
};

const mapValues = (object, convert) => {
  const out = {};
  Object.keys(object || {}).forEach((key) => {
    out[key] = convert(object[key]);
  });
  return out;
};

// The form holds the app content as text; a null from the service is a blank field.
const displayToForm = (value) => {
  const display = value && typeof value === "object" ? value : DEFAULT_DISPLAY;
  const hero = display.hero && typeof display.hero === "object" ? display.hero : null;
  // The upgrade banner (8 Oct 2026) is absent on a draft from before: no banner.
  const banner = display.upgradeBanner && typeof display.upgradeBanner === "object" ? display.upgradeBanner : null;
  const details = display.details && typeof display.details === "object" ? display.details : {};
  return {
    tagline: str(display.tagline),
    bestValue: display.bestValue === true,
    heroTitle: str(hero?.title),
    heroSubtitle: str(hero?.subtitle),
    bannerTitle: str(banner?.title),
    bannerSubtitleMonthly: str(banner?.subtitleMonthly),
    bannerSubtitleOneTime: str(banner?.subtitleOneTime),
    description: str(details.description),
    notCovered: (Array.isArray(details.notCovered) ? details.notCovered : []).map(str),
    termsUrl: str(display.termsUrl),
  };
};

const segmentToForm = (item, defaultKey) => ({
  key: str(item?.key),
  name: str(item?.name),
  isNew: false,
  keyEdited: false,
  isDefault: str(item?.key) !== "" && item?.key === defaultKey,
  models: (Array.isArray(item?.models) ? item.models : []).filter((slug) => typeof slug === "string"),
  oneTime: rupees(item?.prices?.oneTimeMinor),
  dealerMargin: rupees(item?.dealerMarginMinor),
  seriesRank: whole(item?.seriesRank),
  monthly: mapValues(item?.prices?.monthly, rupees),
  addons: mapValues(item?.prices?.addons, rupees),
});

export const formFromContent = (content) => {
  const settings = content?.settings || {};
  const rule = content?.pricingRule || {};
  const coupon = rule.couponRule || null;
  // Absent or null: no segments, and the plan prices as before.
  const segments = rule.segments && typeof rule.segments === "object" ? rule.segments : null;
  return {
    displayName: str(content?.displayName),
    tierRank: whole(content?.tierRank),
    termsVersion: str(content?.termsVersion),
    display: displayToForm(content?.display),
    benefits: (content?.benefits || []).map(benefitToForm),
    tenures: (content?.tenures || []).map(whole),
    settings: {
      eligibilityDays: whole(settings.eligibilityDays),
      upgradeWindowDays: whole(settings.upgradeWindowDays),
      graceDays: whole(settings.graceDays),
      refundWindowDays: whole(settings.refundWindowDays),
      refundRequiresNoUse: settings.refundRequiresNoUse === true,
      extensionGrant: str(settings.extensionGrant) || "paid_months_capped",
      pendingExpiryHours: whole(settings.pendingExpiryHours),
      active: settings.inactive !== true,
      // Not editable here, but carried through so saving a late version keeps them.
      cohort: str(settings.cohort),
      offerEndsOn: str(settings.offerEndsOn),
    },
    pricingRule: {
      baseBps: percent(rule.baseBps),
      floorMinor: rupees(rule.floorMinor),
      capMinor: rupees(rule.capMinor),
      roundingEndingMinor: rupees(rule.roundingEndingMinor),
      roundingStepMinor: rupees(rule.roundingStepMinor),
      monthlyUpliftBps: percent(rule.monthlyUpliftBps),
      monthlyRoundingEndingMinor: rupees(rule.monthlyRoundingEndingMinor),
      monthlyRoundingStepMinor: rupees(rule.monthlyRoundingStepMinor),
      tenureFactors: mapValues(rule.tenureFactors, percent),
      addonRates: mapValues(rule.addonRates, (rate) => ({
        bps: percent(rate?.bps),
        floorMinor: rupees(rate?.floorMinor),
        capMinor: rupees(rate?.capMinor),
      })),
      couponEnabled: coupon !== null,
      couponRule: {
        bps: percent(coupon?.bps),
        mode: str(coupon?.mode) || "truncate_major",
        roundingEndingMinor: rupees(coupon?.roundingEndingMinor),
        roundingStepMinor: rupees(coupon?.roundingStepMinor),
        validityMonths: whole(coupon?.validityMonths),
        basis: str(coupon?.basis) || "cycle_mrp",
      },
      taxRateBps: rule.taxRateBps === null || rule.taxRateBps === undefined ? "" : percent(rule.taxRateBps),
      cycleValueBasis: str(rule.cycleValueBasis) || "cycle_mrp",
      // 14 Oct 2026: what a dealer earns out of the plan on a bike it sold; absent reads as the
      // service defaults (500 / 1,000 rupees).
      dealerMarginMinor: rupees(Number.isInteger(rule.dealerMarginMinor) ? rule.dealerMarginMinor : 50000),
      dealerMarginOverrideMinor: rupees(
        Number.isInteger(rule.dealerMarginOverrideMinor) ? rule.dealerMarginOverrideMinor : 100000
      ),
      segmentsEnabled: segments !== null,
      // Whether the service may hold segments for this draft: it had some when loaded, or the
      // person turned them on here. Only then does "off" go as `segments: null`; otherwise the key
      // is left out, so an em-platform from before segments never sees it.
      segmentsSent: segments !== null,
      segments: (segments !== null && Array.isArray(segments.items) ? segments.items : []).map((item) =>
        segmentToForm(item, segments.defaultKey)
      ),
    },
    overrides: (content?.overrides || []).map((override) => ({
      productGroup: str(override?.productGroup),
      oneTime: rupees(override?.prices?.oneTimeMinor),
      couponValue: rupees(override?.prices?.couponValueMinor),
      monthly: mapValues(override?.prices?.monthly, rupees),
      addons: mapValues(override?.prices?.addons, rupees),
    })),
  };
};

// ------------------------------------------------------------------ form -> content

// Returns { content, invalid }. `invalid` is true when a number the content needs cannot be read
// (blank or half typed); such a form is not saved or previewed, and validateDraftForm says why.
//
// Values the form no longer offers are left out: an add-on rate or an override add-on price for a
// benefit that is not paid upfront, and an override coupon value when there is no coupon benefit.
// Their inputs are hidden, so the person could neither see nor clear them, and the service would
// refuse the draft for them. The form keeps them, so switching the benefit back shows them again.
// `keepHidden` builds the content with them in (used once on load, to see whether the stored draft
// still holds some).
export const contentFromForm = (form, { keepHidden = false } = {}) => {
  let invalid = false;
  const upfrontKeys = new Set(upfrontBenefits(form).map((benefit) => benefit.key));
  const offersAddon = (key) => keepHidden || upfrontKeys.has(key);
  const offersCoupon = keepHidden || hasCouponBenefit(form);
  const need = (value) => {
    if (value === null) {
      invalid = true;
      return 0;
    }
    return value;
  };
  const wholeOrNull = (text) => (text.trim() === "" ? null : need(wholeToInt(text)));
  // A blank text input is sent as null (the contract's "or null"), never as "". The one-line app
  // texts are trimmed: the service refuses surrounding spaces, and a trailing one is easy to leave
  // while typing. The description is sent as typed (its line breaks are its layout).
  const textOrNull = (text) => (text.trim() === "" ? null : text.trim());

  const benefits = form.benefits.map((benefit) => {
    let unlock;
    if (benefit.unlockType === "paid_months") {
      unlock = { type: "paid_months", months: need(wholeToInt(benefit.unlockMonths)) };
      if (benefit.unlockImmediate) {
        unlock.immediateOnOneTime = true;
      }
    } else if (benefit.unlockType === "months_after_start") {
      unlock = { type: "months_after_start", months: need(wholeToInt(benefit.unlockMonths)) };
    } else if (benefit.unlockType === "date") {
      unlock = { type: "date", on: benefit.unlockDate };
    } else {
      unlock = { type: "purchase" };
    }
    const sent = { key: benefit.key, kind: benefit.kind };
    // Title and label stand in for each other on input: a blank one is left out and the service
    // copies the other. With both blank the label goes as typed, so the service names the field.
    const titleTyped = benefit.title.trim() !== "";
    if (benefit.label.trim() !== "" || !titleTyped) {
      sent.label = benefit.label;
    }
    if (titleTyped) {
      sent.title = benefit.title.trim();
    }
    // A free service's worth is optional: blank leaves it out, and the app's card then says
    // "Complimentary". Any other kind never sends one (the service refuses it).
    const worth = typeof benefit.worth === "string" ? benefit.worth : "";
    const worthPart =
      benefit.kind === "free_service" && worth.trim() !== "" ? { worthMinor: need(rupeesToMinor(worth)) } : {};
    return {
      ...sent,
      subtitle: textOrNull(benefit.subtitle),
      chargeModel: benefit.chargeModel,
      termMonths: wholeOrNull(benefit.termMonths),
      followsPlanState: benefit.followsPlanState,
      unlock,
      activation: benefit.activation,
      ...worthPart,
      ...(benefit.kind === "roadside" && benefit.chargeModel === "included" && (benefit.bundledWith || "") !== ""
        ? { bundledWith: benefit.bundledWith }
        : {}),
    };
  });

  const rule = form.pricingRule;
  const held = heldTenures(form);
  const tenureFactors = {};
  Object.keys(rule.tenureFactors).forEach((months) => {
    const key = contentTenureKey(months, held);
    if (key !== null && rule.tenureFactors[months].trim() !== "") {
      tenureFactors[key] = need(percentToBps(rule.tenureFactors[months]));
    }
  });
  const addonRates = {};
  Object.keys(rule.addonRates).forEach((key) => {
    if (!offersAddon(key)) {
      return;
    }
    const rate = rule.addonRates[key];
    // All three blank means no rate yet: left out, and the service reports it as missing.
    if (rate.bps.trim() !== "" || rate.floorMinor.trim() !== "" || rate.capMinor.trim() !== "") {
      addonRates[key] = {
        bps: need(percentToBps(rate.bps)),
        floorMinor: need(rupeesToMinor(rate.floorMinor)),
        capMinor: need(rupeesToMinor(rate.capMinor)),
      };
    }
  });
  const couponRule = rule.couponEnabled
    ? {
        bps: need(percentToBps(rule.couponRule.bps)),
        mode: rule.couponRule.mode,
        roundingEndingMinor: need(rupeesToMinor(rule.couponRule.roundingEndingMinor)),
        roundingStepMinor: need(rupeesToMinor(rule.couponRule.roundingStepMinor)),
        validityMonths: wholeOrNull(rule.couponRule.validityMonths),
        basis: rule.couponRule.basis,
      }
    : null;

  // The hero is null when both its halves are blank. Its halves are not nullable, so a blank one
  // beside a typed one goes as "" and the service reports it under that field.
  const display = form.display;
  const heroBlank = display.heroTitle.trim() === "" && display.heroSubtitle.trim() === "";
  // Likewise the upgrade banner: null when all three are blank, else each as text.
  const bannerBlank = [display.bannerTitle, display.bannerSubtitleMonthly, display.bannerSubtitleOneTime].every(
    (value) => value.trim() === ""
  );

  const overrides = form.overrides.map((override) => {
    const prices = {};
    if (override.oneTime.trim() !== "") {
      prices.oneTimeMinor = need(rupeesToMinor(override.oneTime));
    }
    const monthly = {};
    Object.keys(override.monthly).forEach((months) => {
      const key = contentTenureKey(months, held);
      if (key !== null && override.monthly[months].trim() !== "") {
        monthly[key] = need(rupeesToMinor(override.monthly[months]));
      }
    });
    if (Object.keys(monthly).length > 0) {
      prices.monthly = monthly;
    }
    const addons = {};
    Object.keys(override.addons).forEach((key) => {
      if (offersAddon(key) && override.addons[key].trim() !== "") {
        addons[key] = need(rupeesToMinor(override.addons[key]));
      }
    });
    if (Object.keys(addons).length > 0) {
      prices.addons = addons;
    }
    if (offersCoupon && override.couponValue.trim() !== "") {
      prices.couponValueMinor = need(rupeesToMinor(override.couponValue));
    }
    return { productGroup: override.productGroup, prices };
  });

  // Bike segments: priced like an override (blank monthly or add-on prices are left out), but the
  // one-time price and the dealer margin are required.
  let segmentsPart = {};
  if (rule.segmentsEnabled) {
    const defaultSegment = rule.segments.find((segment) => segment.isDefault);
    segmentsPart = {
      segments: {
        defaultKey: defaultSegment ? defaultSegment.key : "",
        items: rule.segments.map((segment) => {
          const prices = { oneTimeMinor: need(rupeesToMinor(segment.oneTime)) };
          const monthly = {};
          Object.keys(segment.monthly).forEach((months) => {
            const key = contentTenureKey(months, held);
            if (key !== null && segment.monthly[months].trim() !== "") {
              monthly[key] = need(rupeesToMinor(segment.monthly[months]));
            }
          });
          if (Object.keys(monthly).length > 0) {
            prices.monthly = monthly;
          }
          const addons = {};
          Object.keys(segment.addons).forEach((key) => {
            if (offersAddon(key) && segment.addons[key].trim() !== "") {
              addons[key] = need(rupeesToMinor(segment.addons[key]));
            }
          });
          if (Object.keys(addons).length > 0) {
            prices.addons = addons;
          }
          return {
            key: segment.key,
            name: segment.name,
            models: [...segment.models],
            prices,
            dealerMarginMinor: need(rupeesToMinor(segment.dealerMargin)),
            // Blank: no rank, and a coupon from this segment keeps the placeholder rank 1.
            ...(segment.seriesRank.trim() === "" ? {} : { seriesRank: need(wholeToInt(segment.seriesRank)) }),
          };
        }),
      },
    };
  } else if (rule.segmentsSent) {
    segmentsPart = { segments: null };
  }

  const content = {
    displayName: form.displayName,
    tierRank: need(wholeToInt(form.tierRank)),
    termsVersion: form.termsVersion,
    display: {
      tagline: textOrNull(display.tagline),
      bestValue: display.bestValue,
      hero: heroBlank
        ? null
        : {
            title: display.heroTitle.trim(),
            subtitle: display.heroSubtitle.trim(),
          },
      upgradeBanner: bannerBlank
        ? null
        : {
            title: display.bannerTitle.trim(),
            subtitleMonthly: display.bannerSubtitleMonthly.trim(),
            subtitleOneTime: display.bannerSubtitleOneTime.trim(),
          },
      details: {
        description: display.description.trim() === "" ? null : display.description,
        notCovered: display.notCovered.map((line) => line.trim()),
      },
      termsUrl: textOrNull(display.termsUrl),
    },
    benefits,
    tenures: form.tenures.map((tenure) => need(wholeToInt(tenure))),
    settings: {
      eligibilityDays: need(wholeToInt(form.settings.eligibilityDays)),
      upgradeWindowDays: need(wholeToInt(form.settings.upgradeWindowDays)),
      graceDays: need(wholeToInt(form.settings.graceDays)),
      refundWindowDays: need(wholeToInt(form.settings.refundWindowDays)),
      refundRequiresNoUse: form.settings.refundRequiresNoUse,
      extensionGrant: form.settings.extensionGrant,
      pendingExpiryHours: need(wholeToInt(form.settings.pendingExpiryHours)),
      ...(form.settings.active === false ? { inactive: true } : {}),
      ...(form.settings.cohort ? { cohort: form.settings.cohort } : {}),
      ...(form.settings.offerEndsOn ? { offerEndsOn: form.settings.offerEndsOn } : {}),
    },
    pricingRule: {
      baseBps: need(percentToBps(rule.baseBps)),
      floorMinor: need(rupeesToMinor(rule.floorMinor)),
      capMinor: need(rupeesToMinor(rule.capMinor)),
      roundingEndingMinor: need(rupeesToMinor(rule.roundingEndingMinor)),
      roundingStepMinor: need(rupeesToMinor(rule.roundingStepMinor)),
      monthlyUpliftBps: need(percentToBps(rule.monthlyUpliftBps)),
      monthlyRoundingEndingMinor: need(rupeesToMinor(rule.monthlyRoundingEndingMinor)),
      monthlyRoundingStepMinor: need(rupeesToMinor(rule.monthlyRoundingStepMinor)),
      tenureFactors,
      addonRates,
      couponRule,
      taxRateBps: rule.taxRateBps.trim() === "" ? null : need(percentToBps(rule.taxRateBps)),
      cycleValueBasis: rule.cycleValueBasis,
      dealerMarginMinor: need(rupeesToMinor(rule.dealerMarginMinor)),
      dealerMarginOverrideMinor: need(rupeesToMinor(rule.dealerMarginOverrideMinor)),
      ...segmentsPart,
    },
    overrides,
  };
  return { content, invalid };
};

// ------------------------------------------------------------------ structure helpers

// A benefit key rename keeps its add-on rate and override add-on prices under the new key.
export const renameBenefitKey = (form, index, newKey) => {
  const oldKey = form.benefits[index].key;
  const benefits = form.benefits.map((benefit, i) => (i === index ? { ...benefit, key: newKey } : benefit));
  const stillUsed = benefits.some((benefit, i) => i !== index && benefit.key === oldKey);
  if (oldKey === newKey || oldKey === "" || newKey === "" || stillUsed) {
    return { ...form, benefits };
  }
  const moveKey = (object) => {
    if (!Object.prototype.hasOwnProperty.call(object, oldKey) || Object.prototype.hasOwnProperty.call(object, newKey)) {
      return object;
    }
    const moved = {};
    Object.keys(object).forEach((key) => {
      moved[key === oldKey ? newKey : key] = object[key];
    });
    return moved;
  };
  return {
    ...form,
    benefits,
    pricingRule: {
      ...form.pricingRule,
      addonRates: moveKey(form.pricingRule.addonRates),
      segments: form.pricingRule.segments.map((segment) => ({ ...segment, addons: moveKey(segment.addons) })),
    },
    overrides: form.overrides.map((override) => ({ ...override, addons: moveKey(override.addons) })),
  };
};

// Removing a benefit removes the add-on rate and override add-on prices that belonged to it.
export const removeBenefit = (form, index) => {
  const removedKey = form.benefits[index].key;
  const benefits = form.benefits.filter((_, i) => i !== index);
  const stillUsed = benefits.some((benefit) => benefit.key === removedKey);
  const without = (object) => {
    if (stillUsed || !Object.prototype.hasOwnProperty.call(object, removedKey)) {
      return object;
    }
    const copy = { ...object };
    delete copy[removedKey];
    return copy;
  };
  return {
    ...form,
    benefits,
    pricingRule: {
      ...form.pricingRule,
      addonRates: without(form.pricingRule.addonRates),
      segments: form.pricingRule.segments.map((segment) => ({ ...segment, addons: without(segment.addons) })),
    },
    overrides: form.overrides.map((override) => ({ ...override, addons: without(override.addons) })),
  };
};

// Editing a tenure's months keeps its tenure factor and override monthly prices under the new value.
export const renameTenure = (form, index, newValue) => {
  const oldValue = form.tenures[index];
  const tenures = form.tenures.map((tenure, i) => (i === index ? newValue : tenure));
  const stillUsed = tenures.some((tenure, i) => i !== index && tenure === oldValue);
  if (oldValue === newValue || oldValue === "" || newValue === "" || stillUsed) {
    return { ...form, tenures };
  }
  const moveKey = (object) => {
    if (!Object.prototype.hasOwnProperty.call(object, oldValue) || Object.prototype.hasOwnProperty.call(object, newValue)) {
      return object;
    }
    const moved = {};
    Object.keys(object).forEach((key) => {
      moved[key === oldValue ? newValue : key] = object[key];
    });
    return moved;
  };
  return {
    ...form,
    tenures,
    pricingRule: {
      ...form.pricingRule,
      tenureFactors: moveKey(form.pricingRule.tenureFactors),
      segments: form.pricingRule.segments.map((segment) => ({ ...segment, monthly: moveKey(segment.monthly) })),
    },
    overrides: form.overrides.map((override) => ({ ...override, monthly: moveKey(override.monthly) })),
  };
};

// Removing a tenure removes the tenure factor and the override monthly prices that belonged to it.
export const removeTenure = (form, index) => {
  const removed = form.tenures[index];
  const tenures = form.tenures.filter((_, i) => i !== index);
  const stillUsed = tenures.some((tenure) => tenure === removed);
  const without = (object) => {
    if (stillUsed || !Object.prototype.hasOwnProperty.call(object, removed)) {
      return object;
    }
    const copy = { ...object };
    delete copy[removed];
    return copy;
  };
  return {
    ...form,
    tenures,
    pricingRule: {
      ...form.pricingRule,
      tenureFactors: without(form.pricingRule.tenureFactors),
      segments: form.pricingRule.segments.map((segment) => ({ ...segment, monthly: without(segment.monthly) })),
    },
    overrides: form.overrides.map((override) => ({ ...override, monthly: without(override.monthly) })),
  };
};

export const upfrontBenefits = (form) =>
  form.benefits.filter((benefit) => benefit.chargeModel === "upfront" && benefit.key !== "");

export const hasCouponBenefit = (form) => form.benefits.some((benefit) => benefit.kind === "coupon");

// ------------------------------------------------------------------ validation

// Returns { [path]: message } for the rules that need no other tier. The service re-checks all of
// them (and the cross-tier ones); this only gives an answer under the field while the person types.
// `options.productGroupNames` is the OMS product group list; pass it only when it was loaded and
// is not empty, and a group that is not in it is refused (the quote matches the name exactly).
// `options.models` is the website model list ({ slug, name }), used only to name a model in a message.
export const UNKNOWN_PRODUCT_GROUP_MESSAGE =
  "Pick a product group from the list; names must match the OMS exactly.";

export const validateDraftForm = (form, options = {}) => {
  const productGroupNames = Array.isArray(options.productGroupNames) ? options.productGroupNames : null;
  const models = Array.isArray(options.models) ? options.models : [];
  const errors = {};
  const put = (path) => (message) => {
    if (message && errors[path] === undefined) {
      errors[path] = message;
    }
  };
  const fail = (path, message) => put(path)(message);
  const text = (path, value, label, extra) =>
    CheckValid(value, { type: "warranty_text", label, error: put(path), ...extra });
  const wholeNumber = (path, value, label, min, max) =>
    CheckValid(value, { type: "warranty_whole_number", label, min, max, error: put(path) });
  const money = (path, value, label) =>
    CheckValid(value, { type: "warranty_rupees", label, error: put(path) });
  const rate = (path, value, label) =>
    CheckValid(value, { type: "warranty_percent", label, error: put(path) });
  const optionalMoney = (path, value, label) => {
    if (value.trim() !== "") {
      money(path, value, label);
    }
  };

  // 1. basics
  text("displayName", form.displayName, "Display name", { max: 60 });
  text("termsVersion", form.termsVersion, "Terms version", {
    max: 64,
    pattern: TERMS_PATTERN,
    patternMessage: "Use letters, digits, dots, dashes and underscores only",
  });
  wholeNumber("tierRank", form.tierRank, "Tier rank", 1, 99);

  // 1b. app content
  const placeholders = hasCouponBenefit(form) ? ["tierName", "couponValue"] : ["tierName"];
  // {coverYears} (8 Oct 2026): the standard motor cover plus this tier's motor extension, in years.
  const coverYears = form.benefits.some((benefit) => benefit.kind === "motor_extension") ? ["coverYears"] : [];
  const heroPlaceholders = [...placeholders, ...coverYears];
  const bannerPlaceholders = heroPlaceholders;
  const displayText = (path, value, label, max, extra) =>
    CheckValid(value, { type: "warranty_display_text", label, max, error: put(path), ...extra });
  const display = form.display;
  displayText("display.tagline", display.tagline, "Tagline", DISPLAY_LIMITS.tagline, { placeholders });
  displayText("display.hero.title", display.heroTitle, "Hero title", DISPLAY_LIMITS.heroTitle, {
    placeholders: heroPlaceholders,
  });
  displayText("display.hero.subtitle", display.heroSubtitle, "Hero subtitle", DISPLAY_LIMITS.heroSubtitle, {
    placeholders: heroPlaceholders,
  });
  if (display.heroTitle.trim() === "" && display.heroSubtitle.trim() !== "") {
    fail("display.hero.title", "Add a hero title too, or clear the subtitle");
  } else if (display.heroTitle.trim() !== "" && display.heroSubtitle.trim() === "") {
    fail("display.hero.subtitle", "Add a hero subtitle too, or clear the title");
  }
  // The upgrade banner: all three texts, or none. {coverYears} needs a motor extension, each
  // per-rider amount only in its own subtitle, {extraPerMonth} only with a monthly tenure.
  const bannerTexts = [
    ["display.upgradeBanner.title", display.bannerTitle, "Banner title", DISPLAY_LIMITS.bannerTitle, bannerPlaceholders],
    [
      "display.upgradeBanner.subtitleMonthly",
      display.bannerSubtitleMonthly,
      "Monthly subtitle",
      DISPLAY_LIMITS.bannerSubtitle,
      form.tenures.length > 0 ? [...bannerPlaceholders, "extraPerMonth"] : bannerPlaceholders,
    ],
    [
      "display.upgradeBanner.subtitleOneTime",
      display.bannerSubtitleOneTime,
      "One-time subtitle",
      DISPLAY_LIMITS.bannerSubtitle,
      [...bannerPlaceholders, "upgradePrice"],
    ],
  ];
  const bannerTyped = bannerTexts.some(([, value]) => value.trim() !== "");
  bannerTexts.forEach(([path, value, label, max, allowed]) =>
    displayText(path, value, label, max, { placeholders: allowed, required: bannerTyped })
  );
  displayText("display.details.description", display.description, "Description", DISPLAY_LIMITS.description);
  if (display.notCovered.length > DISPLAY_LIMITS.notCoveredItems) {
    fail("display.details.notCovered", `At most ${DISPLAY_LIMITS.notCoveredItems} lines`);
  }
  display.notCovered.forEach((item, index) =>
    displayText(`display.details.notCovered.${index}`, item, "This line", DISPLAY_LIMITS.notCoveredItem, {
      required: true,
    })
  );
  CheckValid(display.termsUrl, {
    type: "warranty_https_url",
    label: "Terms link",
    max: DISPLAY_LIMITS.termsUrl,
    error: put("display.termsUrl"),
  });

  // 2. benefits
  if (form.benefits.length === 0) {
    fail("benefits", "Add at least one benefit");
  }
  const seenKeys = {};
  const extensionSeen = {};
  let couponCount = 0;
  form.benefits.forEach((benefit, index) => {
    const at = (field) => `benefits.${index}.${field}`;
    text(at("key"), benefit.key, "Key", {
      max: 32,
      pattern: TIER_KEY_LIKE,
      patternMessage: "Start with a lower case letter; use lower case letters, digits and underscores",
    });
    if (benefit.key !== "") {
      if (seenKeys[benefit.key]) {
        fail(at("key"), "Another benefit has this key");
      }
      seenKeys[benefit.key] = true;
    }
    // A blank label is fine when the title is typed: the service copies the title into it.
    if (benefit.label.trim() !== "" || benefit.title.trim() === "") {
      text(at("label"), benefit.label, "Label", { max: 80 });
    }
    displayText(at("title"), benefit.title, "Title", DISPLAY_LIMITS.benefitTitle, { placeholders });
    displayText(at("subtitle"), benefit.subtitle, "Subtitle", DISPLAY_LIMITS.benefitSubtitle, { placeholders });
    if (benefit.kind === "free_service" && typeof benefit.worth === "string") {
      optionalMoney(at("worthMinor"), benefit.worth, "Worth");
    }
    if (benefit.termMonths.trim() !== "") {
      wholeNumber(at("termMonths"), benefit.termMonths, "Term", 1, 120);
    }
    if (benefit.unlockType === "paid_months") {
      wholeNumber(at("unlock.months"), benefit.unlockMonths, "Paid months", 0, 120);
    } else if (benefit.unlockType === "months_after_start") {
      wholeNumber(at("unlock.months"), benefit.unlockMonths, "Months after purchase", 0, 120);
    } else if (benefit.unlockType === "date" && /^\d{4}-\d{2}-\d{2}$/.test(benefit.unlockDate) === false) {
      fail(at("unlock.on"), "Enter the date as YYYY-MM-DD");
    }
    if (EXTENSION_KINDS.includes(benefit.kind)) {
      if (benefit.termMonths.trim() === "") {
        fail(at("termMonths"), "An extension needs its term in months");
      }
      if (extensionSeen[benefit.kind]) {
        fail(at("kind"), "Only one benefit of this kind is allowed; cover months would add up");
      }
      extensionSeen[benefit.kind] = true;
    }
    if (benefit.kind === "theft" || benefit.kind === "roadside") {
      const bundled =
        benefit.kind === "roadside" && benefit.chargeModel === "included" && (benefit.bundledWith || "") !== "";
      if (benefit.chargeModel !== "upfront" && !bundled) {
        fail(at("chargeModel"), "Theft and roadside are paid upfront (or roadside charged with the theft line)");
      }
      if (benefit.termMonths.trim() === "") {
        fail(at("termMonths"), "This cover needs a term in months, or it never ends");
      }
    }
    if (benefit.chargeModel === "upfront" && benefit.kind !== "theft" && benefit.kind !== "roadside") {
      fail(at("chargeModel"), "Only theft and roadside can be paid upfront");
    }
    if ((benefit.kind === "coupon" || benefit.kind === "free_service") && benefit.activation !== "automatic") {
      fail(at("activation"), "This kind of benefit must activate automatically");
    }
    if (benefit.kind === "coupon") {
      couponCount += 1;
      if (couponCount > 1) {
        fail(at("kind"), "Only one coupon is allowed");
      }
    }
  });

  // 3. tenures
  const seenTenures = {};
  form.tenures.forEach((tenure, index) => {
    wholeNumber(`tenures.${index}`, tenure, "Tenure", 1, 60);
    if (tenure.trim() !== "") {
      // "12" and "012" are the same tenure (the content carries the number).
      const same = wholeToInt(tenure) === null ? tenure.trim() : String(wholeToInt(tenure));
      if (seenTenures[same]) {
        fail(`tenures.${index}`, "This tenure is listed twice");
      }
      seenTenures[same] = true;
    }
  });

  // 4. settings
  const settings = form.settings;
  wholeNumber("settings.eligibilityDays", settings.eligibilityDays, "Eligibility days", 0, ELIGIBILITY_DAYS_MAX);
  wholeNumber("settings.upgradeWindowDays", settings.upgradeWindowDays, "Upgrade window", 0, 365);
  wholeNumber("settings.graceDays", settings.graceDays, "Grace days", 0, 365);
  wholeNumber("settings.refundWindowDays", settings.refundWindowDays, "Refund window", 0, 365);
  wholeNumber("settings.pendingExpiryHours", settings.pendingExpiryHours, "Pending expiry", 1, 168);

  // 5. pricing rule
  const rule = form.pricingRule;
  rate("pricingRule.baseBps", rule.baseBps, "Base rate");
  money("pricingRule.floorMinor", rule.floorMinor, "Floor");
  money("pricingRule.capMinor", rule.capMinor, "Cap");
  money("pricingRule.dealerMarginMinor", rule.dealerMarginMinor, "Dealer margin (normal bikes)");
  money("pricingRule.dealerMarginOverrideMinor", rule.dealerMarginOverrideMinor, "Dealer margin (override groups)");
  const floor = rupeesToMinor(rule.floorMinor);
  const cap = rupeesToMinor(rule.capMinor);
  if (floor !== null && cap !== null && floor > cap) {
    fail("pricingRule.floorMinor", "Floor cannot be above the cap");
  }
  const rounding = (stepPath, endingPath, step, ending, label) => {
    money(endingPath, ending, `${label} ending`);
    money(stepPath, step, `${label} step`);
    const stepMinor = rupeesToMinor(step);
    const endingMinor = rupeesToMinor(ending);
    if (stepMinor !== null && stepMinor < 1) {
      fail(stepPath, `${label} step must be at least 0.01`);
    } else if (stepMinor !== null && endingMinor !== null && endingMinor >= stepMinor) {
      fail(endingPath, `${label} ending must be below its step`);
    }
  };
  rounding("pricingRule.roundingStepMinor", "pricingRule.roundingEndingMinor", rule.roundingStepMinor, rule.roundingEndingMinor, "Rounding");
  rate("pricingRule.monthlyUpliftBps", rule.monthlyUpliftBps, "Monthly uplift");
  rounding(
    "pricingRule.monthlyRoundingStepMinor",
    "pricingRule.monthlyRoundingEndingMinor",
    rule.monthlyRoundingStepMinor,
    rule.monthlyRoundingEndingMinor,
    "Monthly rounding"
  );
  const held = heldTenures(form);
  Object.keys(rule.tenureFactors).forEach((months) => {
    if (contentTenureKey(months, held) !== null && rule.tenureFactors[months].trim() !== "") {
      rate(`pricingRule.tenureFactors.${months}`, rule.tenureFactors[months], "Tenure factor");
    }
  });
  if (rule.taxRateBps.trim() !== "") {
    rate("pricingRule.taxRateBps", rule.taxRateBps, "Tax rate");
  }
  upfrontBenefits(form).forEach((benefit) => {
    const addon = rule.addonRates[benefit.key] || { bps: "", floorMinor: "", capMinor: "" };
    const base = `pricingRule.addonRates.${benefit.key}`;
    if (addon.bps.trim() === "" && addon.floorMinor.trim() === "" && addon.capMinor.trim() === "") {
      fail(base, `Add a rate, floor and cap for ${benefit.label || benefit.key}`);
      return;
    }
    rate(`${base}.bps`, addon.bps, "Rate");
    money(`${base}.floorMinor`, addon.floorMinor, "Floor");
    money(`${base}.capMinor`, addon.capMinor, "Cap");
    const addonFloor = rupeesToMinor(addon.floorMinor);
    const addonCap = rupeesToMinor(addon.capMinor);
    if (addonFloor !== null && addonCap !== null && addonFloor > addonCap) {
      fail(`${base}.floorMinor`, "Floor cannot be above the cap");
    }
  });
  const couponBenefit = hasCouponBenefit(form);
  if (couponBenefit && !rule.couponEnabled) {
    fail("pricingRule.couponRule", "A coupon benefit needs a coupon rule");
  } else if (!couponBenefit && rule.couponEnabled) {
    fail("pricingRule.couponRule", "There is no coupon benefit, so there is no coupon rule");
  }
  if (rule.couponEnabled) {
    rate("pricingRule.couponRule.bps", rule.couponRule.bps, "Coupon rate");
    const couponBps = percentToBps(rule.couponRule.bps);
    if (couponBps !== null && couponBps < 1) {
      fail("pricingRule.couponRule.bps", "Coupon rate must be above 0");
    }
    rounding(
      "pricingRule.couponRule.roundingStepMinor",
      "pricingRule.couponRule.roundingEndingMinor",
      rule.couponRule.roundingStepMinor,
      rule.couponRule.roundingEndingMinor,
      "Coupon rounding"
    );
    if (rule.couponRule.validityMonths.trim() !== "") {
      wholeNumber(
        "pricingRule.couponRule.validityMonths",
        rule.couponRule.validityMonths,
        "Validity",
        1,
        VALIDITY_MONTHS_MAX
      );
    }
  }

  // 7. overrides
  const seenGroups = {};
  form.overrides.forEach((override, index) => {
    const at = (field) => `overrides.${index}.${field}`;
    // An OMS name picked from the list is taken exactly as the OMS stores it, spaces and all: the
    // quote matches it exactly, so only its length is checked here.
    const listed = productGroupNames !== null && productGroupNames.includes(override.productGroup);
    if (listed) {
      if (override.productGroup.length > 80) {
        fail(at("productGroup"), "Product group must be at most 80 characters");
      }
    } else {
      text(at("productGroup"), override.productGroup, "Product group", { max: 80 });
    }
    if (override.productGroup !== "") {
      if (seenGroups[override.productGroup]) {
        fail(at("productGroup"), "Another override has this product group");
      }
      seenGroups[override.productGroup] = true;
      if (productGroupNames !== null && !productGroupNames.includes(override.productGroup)) {
        fail(at("productGroup"), UNKNOWN_PRODUCT_GROUP_MESSAGE);
      }
    }
    optionalMoney(at("prices.oneTimeMinor"), override.oneTime, "One-time price");
    Object.keys(override.monthly).forEach((months) => {
      if (contentTenureKey(months, held) !== null) {
        optionalMoney(at(`prices.monthly.${months}`), override.monthly[months], "Instalment");
      }
    });
    // Only what the form offers (and so what the content carries) is checked; see contentFromForm.
    upfrontBenefits(form).forEach((benefit) => {
      if (override.addons[benefit.key] !== undefined) {
        optionalMoney(at(`prices.addons.${benefit.key}`), override.addons[benefit.key], "Add-on price");
      }
    });
    if (couponBenefit && override.couponValue.trim() !== "") {
      money(at("prices.couponValueMinor"), override.couponValue, "Coupon value");
      if (rupeesToMinor(override.couponValue) !== null && rupeesToMinor(override.couponValue) < 1) {
        fail(at("prices.couponValueMinor"), "Coupon value must be at least 0.01");
      }
    }
  });

  // 8. bike segments
  if (rule.segmentsEnabled) {
    const base = "pricingRule.segments";
    if (rule.segments.length === 0) {
      fail(base, "Add at least one segment, or turn bike segments off");
    } else {
      const defaults = rule.segments.filter((segment) => segment.isDefault).length;
      if (defaults === 0) {
        fail(`${base}.defaultKey`, "Pick the default segment");
      } else if (defaults > 1) {
        fail(`${base}.defaultKey`, "Only one segment can be the default");
      }
    }
    const seenSegmentKeys = {};
    const modelOwner = {};
    rule.segments.forEach((segment, index) => {
      const at = (field) => `${base}.items.${index}.${field}`;
      text(at("name"), segment.name, "Name", { max: 60 });
      text(at("key"), segment.key, "Key", {
        max: 32,
        pattern: TIER_KEY_LIKE,
        patternMessage: "Start with a lower case letter; use lower case letters, digits and underscores",
      });
      if (segment.key !== "") {
        if (seenSegmentKeys[segment.key]) {
          fail(at("key"), "Another segment has this key");
        }
        seenSegmentKeys[segment.key] = true;
      }
      segment.models.forEach((slug) => {
        const owner = modelOwner[slug];
        if (owner !== undefined && owner !== segment) {
          fail(at("models"), `${modelName(models, slug)} is already in ${owner.name.trim() || "another segment"}`);
        } else {
          modelOwner[slug] = segment;
        }
      });
      money(at("prices.oneTimeMinor"), segment.oneTime, "One-time price");
      money(at("dealerMarginMinor"), segment.dealerMargin, "Dealer margin");
      if (segment.seriesRank.trim() !== "") {
        wholeNumber(at("seriesRank"), segment.seriesRank, "Coupon series rank", 1, 99);
      }
      Object.keys(segment.monthly).forEach((months) => {
        if (contentTenureKey(months, held) !== null) {
          optionalMoney(at(`prices.monthly.${months}`), segment.monthly[months], "Instalment");
        }
      });
      upfrontBenefits(form).forEach((benefit) => {
        if (segment.addons[benefit.key] !== undefined) {
          optionalMoney(at(`prices.addons.${benefit.key}`), segment.addons[benefit.key], "Add-on price");
        }
      });
    });
  }
  return errors;
};

// Every path the editor shows a message under, that is a field or a section that is on screen now.
// A server issue or a client error whose path is not here goes to the list at the top of the
// editor, so nothing that blocks Publish can be invisible.
export const knownPaths = (form) => {
  const paths = new Set([
    "displayName",
    "tierRank",
    "termsVersion",
    "benefits",
    "tenures",
    "overrides",
    "pricingRule.couponRule",
    "display",
    "display.tagline",
    "display.bestValue",
    "display.hero",
    "display.hero.title",
    "display.hero.subtitle",
    "display.upgradeBanner",
    "display.upgradeBanner.title",
    "display.upgradeBanner.subtitleMonthly",
    "display.upgradeBanner.subtitleOneTime",
    "display.details",
    "display.details.description",
    "display.details.notCovered",
    "display.termsUrl",
  ]);
  form.display.notCovered.forEach((_, index) => paths.add(`display.details.notCovered.${index}`));
  ["eligibilityDays", "upgradeWindowDays", "graceDays", "refundWindowDays", "extensionGrant", "pendingExpiryHours"].forEach(
    (field) => paths.add(`settings.${field}`)
  );
  [
    "baseBps",
    "floorMinor",
    "capMinor",
    "roundingEndingMinor",
    "roundingStepMinor",
    "monthlyUpliftBps",
    "monthlyRoundingEndingMinor",
    "monthlyRoundingStepMinor",
    "taxRateBps",
  ].forEach((field) => paths.add(`pricingRule.${field}`));
  // With bike segments on, the two plan-wide margins are hidden (still sent): a message about them
  // goes to the list at the top, where it can be seen.
  if (!form.pricingRule.segmentsEnabled) {
    paths.add("pricingRule.dealerMarginMinor");
    paths.add("pricingRule.dealerMarginOverrideMinor");
  }
  if (form.pricingRule.couponEnabled) {
    ["bps", "mode", "roundingEndingMinor", "roundingStepMinor", "validityMonths"].forEach((field) =>
      paths.add(`pricingRule.couponRule.${field}`)
    );
  }
  form.benefits.forEach((benefit, index) => {
    ["key", "kind", "label", "title", "subtitle", "chargeModel", "termMonths", "activation", "unlock.type"].forEach((field) =>
      paths.add(`benefits.${index}.${field}`)
    );
    if (benefit.unlockType === "paid_months" || benefit.unlockType === "months_after_start") {
      paths.add(`benefits.${index}.unlock.months`);
    }
    if (benefit.unlockType === "date") {
      paths.add(`benefits.${index}.unlock.on`);
    }
    paths.add(`benefits.${index}`);
  });
  form.tenures.forEach((tenure, index) => {
    paths.add(`tenures.${index}`);
    paths.add(`pricingRule.tenureFactors.${tenure}`);
  });
  upfrontBenefits(form).forEach((benefit) => {
    const base = `pricingRule.addonRates.${benefit.key}`;
    ["", ".bps", ".floorMinor", ".capMinor"].forEach((tail) => paths.add(base + tail));
  });
  form.overrides.forEach((override, index) => {
    const at = (field) => `overrides.${index}.${field}`;
    paths.add(at("productGroup"));
    paths.add(`overrides.${index}`);
    paths.add(at("prices"));
    paths.add(at("prices.oneTimeMinor"));
    if (hasCouponBenefit(form)) {
      paths.add(at("prices.couponValueMinor"));
    }
    form.tenures.forEach((tenure) => paths.add(at(`prices.monthly.${tenure}`)));
    upfrontBenefits(form).forEach((benefit) => paths.add(at(`prices.addons.${benefit.key}`)));
  });
  paths.add("pricingRule.segments");
  if (form.pricingRule.segmentsEnabled) {
    paths.add("pricingRule.segments.defaultKey");
    paths.add("pricingRule.segments.items");
    form.pricingRule.segments.forEach((segment, index) => {
      const at = (field) => `pricingRule.segments.items.${index}${field}`;
      ["", ".name", ".key", ".models", ".prices", ".prices.oneTimeMinor", ".dealerMarginMinor", ".seriesRank"].forEach((tail) =>
        paths.add(at(tail))
      );
      segment.models.forEach((_, modelIndex) => paths.add(at(`.models.${modelIndex}`)));
      form.tenures.forEach((tenure) => paths.add(at(`.prices.monthly.${tenure}`)));
      upfrontBenefits(form).forEach((benefit) => paths.add(at(`.prices.addons.${benefit.key}`)));
    });
  }
  return paths;
};
